import { create } from "zustand";
import { UserRepository } from "@amityco/ts-sdk";
import { IMember } from "../services/amityService";

const fetchMembersFromAmity = (
  setMembers: (members: IMember[]) => void
): (() => void) | null => {
  let allMembers: IMember[] = [];
  let processedUserIds = new Set<string>();
  let unsubscriber: (() => void) | null = null;

  unsubscriber = UserRepository.getUsers(
    { limit: 100 },
    (userRes) => {
      if (userRes.error) {
        if (unsubscriber) unsubscriber();
        return;
      }

      if (userRes.loading) {
        return;
      }

      const pageMembers = userRes.data
        .filter((user: any) => {
          if (processedUserIds.has(user.userId)) {
            return false;
          }
          processedUserIds.add(user.userId);
          return user.metadata.givenName;
        })
        .map((user: any) => ({
          userId: user.userId,
          displayName: user.metadata.givenName + ' ' + user.metadata.familyName,
          profileImage: user.metadata.imageProfile?.fileUrl,
          profileUniqueId: user.metadata.uniqueProfileId ?? '',
          rioUserIds: [user.metadata.userId, ...(user.metadata.userIds ?? [])].filter(Boolean) as string[],
        }));

      allMembers = [...allMembers, ...pageMembers];

      if (userRes.hasNextPage && userRes.onNextPage) {
        userRes.onNextPage();
      } else {
        setMembers(allMembers);
        if (unsubscriber) unsubscriber();
      }
    }
  );

  return unsubscriber;
};

const useMembers = create<{
  allMembers: IMember[];
  isLoading: boolean;
  getMembers: () => Promise<void>;
  getMemberById: (userId: string) => IMember | undefined;
  getMemberByRioUserId: (rioUserId: string) => IMember | undefined;
  getFilteredMembers: (
    searchTerm: string,
    limit: number,
    offset: number
  ) => IMember[];
  clearCache: () => void;
}>((set, get) => ({
  allMembers: [],
  isLoading: false,
  getMembers: async () => {
    const state = get();
    if (state.allMembers.length > 0) {
      return;
    }

    if (state.isLoading) {
      return;
    }

    set({ isLoading: true });

    return new Promise<void>((resolve, reject) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let isResolved = false;

      const resolveOnce = () => {
        if (!isResolved) {
          isResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          set({ isLoading: false });
          resolve();
        }
      };

      const rejectOnce = (error: any) => {
        if (!isResolved) {
          isResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          set({ isLoading: false });
          reject(error);
        }
      };

      timeoutId = setTimeout(() => {
        rejectOnce(new Error('Timeout ao buscar membros'));
      }, 30000);

      try {
        fetchMembersFromAmity((members: IMember[]) => {
          set({ allMembers: members });
          resolveOnce();
        });
      } catch (error) {
        rejectOnce(error);
      }
    });
  },
  getMemberById: (userId: string): IMember | undefined => {
    return useMembers
      .getState()
      .allMembers.find((member: IMember) => member.userId === userId);
  },
  getMemberByRioUserId: (rioUserId: string): IMember | undefined => {
    return useMembers
      .getState()
      .allMembers.find((member: IMember) => member.rioUserIds.includes(rioUserId));
  },
  getFilteredMembers: (
    searchTerm: string,
    limit: number,
    offset: number
  ): IMember[] => {
    return useMembers
      .getState()
      .allMembers.filter((member: IMember) => {
        const searchTermLower = (searchTerm?.toString() ?? "").toLowerCase();
        return (
          member.displayName?.toLowerCase().includes(searchTermLower) ||
          member.userId?.toLowerCase().includes(searchTermLower) ||
          member.rioUserIds.some(rioUserId => rioUserId.toLowerCase().includes(searchTermLower)) ||
          member.profileUniqueId?.toLowerCase().includes(searchTermLower)
        );
      })
      .slice(offset, offset + limit);
  },
  clearCache: () => {
    set({ allMembers: [], isLoading: false });
  },
}));

export { fetchMembersFromAmity };
export default useMembers;

